import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Paper, Button, IconButton, TextField, Box } from '@mui/material';
import { getIcon } from '../../../utils/IconHandler';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../utils/DateUtils';
import { permissionOptions } from '../../../utils/Enums';
import TableRefreshButton from '../../../components/TableRefreshButton/TableRefreshButton';

const StockMovementsTable = ({ 
    movements = [], 
    paginationModel, 
    onPaginationModelChange, 
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
    searchTerm, 
    onSearchChange, 
    totalRows, 
    onRefresh,
    onAdd, 
    onView, 
    loading 
}) => {
    const { t } = useTranslation();

    const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
    const hasPermission = (permissionId) => userPermissions.includes(permissionId);

    const columns = [
        {
            field: 'date',
            headerName: t('date'),
            flex: 1,
            renderCell: (params) => formatDate(params.value)
        },
        {
            field: 'sourceBranch',
            headerName: t('source_branch'),
            flex: 1.5,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => params.value?.name || '—'
        },
        {
            field: 'destinationBranch',
            headerName: t('destination_branch'),
            flex: 1.5,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => params.value?.name || '—'
        },
        {
            field: 'staff',
            headerName: t('responsible'),
            flex: 1.5,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const staff = params.value;
                return staff ? `${staff.firstName} ${staff.lastName}` : '—';
            }
        },
        {
            field: 'actions',
            headerName: t('actions'),
            flex: 0.8,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <IconButton onClick={() => onView(params.row)}>
                    {getIcon('eye')}
                </IconButton>
            )
        }
    ];

    return (
        <Paper sx={{ padding: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <TextField
                    label={t('search')}
                    placeholder={t('search_by_branch')}
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    sx={{ width: 300 }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TableRefreshButton loading={loading} onClick={onRefresh} />
                    {hasPermission(permissionOptions.find(p => p.label === 'CREATE_MOVEMENTS_STOCK').id) && (
                        <Button variant="outlined" startIcon={getIcon('add')} onClick={onAdd}>
                            {t('add_stock_movement')}
                        </Button>
                    )}
                </Box>
            </Box>

            <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: 800 }}>
                    <DataGrid
                        loading={loading}
                        rows={movements}
                        columns={columns}
                        paginationModel={paginationModel}
                        onPaginationModelChange={onPaginationModelChange}
                        sortModel={sortModel}
                        onSortModelChange={onSortModelChange}
                        sortingMode="server"
                        filterModel={filterModel}
                        onFilterModelChange={onFilterModelChange}
                        filterMode="server"
                        pageSizeOptions={[10, 25, 50]}
                        paginationMode="server"
                        rowCount={totalRows}
                        getRowId={(row) => row.id}
                    />
                </Box>
            </Box>
        </Paper>
    );
};

export default StockMovementsTable;
